import { MultilineInput } from '@amelio/ui-library';
import { Controller, useFormContext } from 'react-hook-form';

import { AspirationQuestionBlock } from './AspirationQuestionBlock';
import { GROWTH_BLOCK_SURFACE_BLOCKERS } from './careerAspirationStyles';
import { NO_BLOCKER, SHORT_NOTES_MAX_LENGTH } from './careerAspirationValidation';
import { useCareerAspirationFormText } from './useCareerAspirationFormText';

import type { CareerAspirationFormValues } from './careerAspirationValidation';
import type { CareerAspirationQuestionProps } from './types';
import type { GrowthBlocker } from '@/types/career-growth';

import { ChoiceChips, toggleInList } from '@/components/ChoiceChips/ChoiceChips';
import { GROWTH_BLOCKERS } from '@/types/career-growth';

const toggleBlocker = (blockers: GrowthBlocker[], blocker: GrowthBlocker): GrowthBlocker[] => {
    if (blocker === NO_BLOCKER) {
        return blockers.includes(NO_BLOCKER) ? [] : [NO_BLOCKER];
    }
    return toggleInList(
        blockers.filter((picked) => picked !== NO_BLOCKER),
        blocker,
    );
};

/** What stands in the way of the employee's growth, picked, then told in their own words. */
export const BlockersQuestion = ({
    dataTestId,
}: Pick<CareerAspirationQuestionProps, 'dataTestId'>) => {
    const { t, optionsOf, errorMessage } = useCareerAspirationFormText();
    const {
        control,
        formState: { errors },
    } = useFormContext<CareerAspirationFormValues>();

    const question = t('performanceProfile.growth.aspiration.blockers');

    return (
        <AspirationQuestionBlock
            title={question}
            className={GROWTH_BLOCK_SURFACE_BLOCKERS}
            dataTestId={`${dataTestId}-blockers`}
        >
            <Controller
                name={'growthBlockers'}
                control={control}
                render={({ field }) => (
                    <ChoiceChips
                        options={optionsOf(
                            GROWTH_BLOCKERS,
                            (blocker) =>
                                `performanceProfile.growth.aspiration.blockerOptions.${blocker}`,
                        )}
                        selected={field.value}
                        onToggle={(value) => field.onChange(toggleBlocker(field.value, value))}
                        ariaLabel={question}
                        tone={'warning'}
                        dataTestId={`${dataTestId}-blocker-chips`}
                    />
                )}
            />

            <Controller
                name={'blockersNotes'}
                control={control}
                render={({ field }) => (
                    <MultilineInput
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        placeholder={t(
                            'performanceProfile.growth.aspiration.form.blockersNotesPlaceholder',
                        )}
                        rows={3}
                        maxLength={SHORT_NOTES_MAX_LENGTH}
                        variant={'white'}
                        error={!!errors.blockersNotes}
                        errorMessage={errorMessage(errors.blockersNotes?.message)}
                        ariaLabel={t(
                            'performanceProfile.growth.aspiration.form.blockersNotesPlaceholder',
                        )}
                        dataTestId={`${dataTestId}-blockers-notes`}
                    />
                )}
            />
        </AspirationQuestionBlock>
    );
};
